import { Icons } from '@commenda/kakashi';
import { useState, type CSSProperties } from 'react';
import { LEGEND_ROWS, type ShapeKey } from '../tax-chart-shapes';

// Match TaxChartNode's stroke so the legend icons read with the same pen
// weight as the chart shapes they're documenting.
const ICON_STROKE = '#8F8B85';
const ICON_STROKE_W = 1;
// Deliberately compact: the in-view legend's footprint is reserved out of the
// chart's fit padding (TaxDiagram.computeFitPadding), so every pixel here is a
// pixel the chart can't use.
const ICON_W = 24;
const ICON_H = 16;

// Inline hex so values survive the consuming app's Tailwind content-scanner
// (same rationale as DiagramToolbar).
const BORDER = '#DEDBD4';
const BG = '#FFFFFF';

const containerStyle: CSSProperties = {
  backgroundColor: BG,
  borderColor: BORDER,
  borderWidth: 0.5,
  borderStyle: 'solid',
  borderRadius: 8,
  padding: 10,
  boxShadow: '0px 2px 4px rgba(0,0,0,0.04)',
};

interface IconProps {
  className?: string;
}

const OvalIcon = ({ className }: IconProps) => (
  <svg width={ICON_W} height={ICON_H} viewBox={`0 0 ${ICON_W} ${ICON_H}`} className={className}>
    <ellipse
      cx={ICON_W / 2}
      cy={ICON_H / 2}
      rx={ICON_W / 2 - ICON_STROKE_W}
      ry={ICON_H / 2 - ICON_STROKE_W}
      fill={BG}
      stroke={ICON_STROKE}
      strokeWidth={ICON_STROKE_W}
    />
  </svg>
);

const TriangleIcon = ({ className }: IconProps) => {
  const inset = ICON_STROKE_W;
  const points = `${ICON_W / 2},${inset} ${ICON_W - inset},${ICON_H - inset} ${inset},${ICON_H - inset}`;
  return (
    <svg width={ICON_W} height={ICON_H} viewBox={`0 0 ${ICON_W} ${ICON_H}`} className={className}>
      <polygon
        points={points}
        fill={BG}
        stroke={ICON_STROKE}
        strokeWidth={ICON_STROKE_W}
        strokeLinejoin="round"
      />
    </svg>
  );
};

const DisregardedIcon = ({ className }: IconProps) => (
  <svg width={ICON_W} height={ICON_H} viewBox={`0 0 ${ICON_W} ${ICON_H}`} className={className}>
    <rect
      x={ICON_STROKE_W / 2}
      y={ICON_STROKE_W / 2}
      width={ICON_W - ICON_STROKE_W}
      height={ICON_H - ICON_STROKE_W}
      rx={4}
      fill={BG}
      stroke={ICON_STROKE}
      strokeWidth={ICON_STROKE_W}
    />
    <ellipse
      cx={ICON_W / 2}
      cy={ICON_H / 2}
      rx={ICON_W / 2 - ICON_STROKE_W}
      ry={ICON_H / 2 - ICON_STROKE_W}
      fill="none"
      stroke={ICON_STROKE}
      strokeWidth={ICON_STROKE_W}
    />
  </svg>
);

const RectangleIcon = ({ className }: IconProps) => (
  <svg width={ICON_W} height={ICON_H} viewBox={`0 0 ${ICON_W} ${ICON_H}`} className={className}>
    <rect
      x={ICON_STROKE_W / 2}
      y={ICON_STROKE_W / 2}
      width={ICON_W - ICON_STROKE_W}
      height={ICON_H - ICON_STROKE_W}
      rx={4}
      fill={BG}
      stroke={ICON_STROKE}
      strokeWidth={ICON_STROKE_W}
    />
  </svg>
);

const RhombusIcon = ({ className }: IconProps) => {
  const inset = ICON_STROKE_W;
  const points = `${ICON_W / 2},${inset} ${ICON_W - inset},${ICON_H / 2} ${ICON_W / 2},${ICON_H - inset} ${inset},${ICON_H / 2}`;
  return (
    <svg width={ICON_W} height={ICON_H} viewBox={`0 0 ${ICON_W} ${ICON_H}`} className={className}>
      <polygon points={points} fill={BG} stroke={ICON_STROKE} strokeWidth={ICON_STROKE_W} strokeLinejoin="round" />
    </svg>
  );
};

export const ICON_FOR_SHAPE: Record<ShapeKey, (props: IconProps) => JSX.Element> = {
  rectangle: RectangleIcon,
  triangle: TriangleIcon,
  disregarded: DisregardedIcon,
  rhombus: RhombusIcon,
  oval: OvalIcon,
};

const ITEMS = LEGEND_ROWS.map(({ shape, label }) => ({ Icon: ICON_FOR_SHAPE[shape], label }));

const ItemsList = () => (
  <ul className="flex flex-col gap-sm items-start justify-center">
    {ITEMS.map(({ Icon, label }) => (
      <li key={label} className="flex w-full items-center gap-sm">
        <Icon className="shrink-0" />
        <span className="txt-extra-small-regular text-text-primary whitespace-nowrap flex-1 min-w-0">
          {label}
        </span>
      </li>
    ))}
  </ul>
);

export interface TaxChartLegendProps {
  // `static` is the chevron-less always-expanded variant we composite into
  // exports. `interactive` is the in-view accordion.
  variant?: 'interactive' | 'static';
}

export const TaxChartLegend = ({ variant = 'interactive' }: TaxChartLegendProps) => {
  const [expanded, setExpanded] = useState(true);
  const isStatic = variant === 'static';

  if (isStatic) {
    return (
      <div style={containerStyle} className="flex flex-col gap-lg items-start justify-center">
        <div className="pr-xs">
          <span className="txt-small-medium text-text-primary whitespace-nowrap">Legend</span>
        </div>
        <ItemsList />
      </div>
    );
  }

  return (
    <div style={containerStyle} className="flex flex-col items-start justify-center">
      <div className="flex items-center justify-between w-full pr-xs">
        <span className="txt-small-medium text-text-primary whitespace-nowrap">Legend</span>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse legend' : 'Expand legend'}
          className="flex items-center justify-center size-3"
        >
          <Icons.ChevronDown
            className={`size-3 text-text-primary transition-transform duration-200 ${expanded ? '' : '-rotate-180'}`}
          />
        </button>
      </div>
      {/* CSS-grid 0fr → 1fr trick: smoothly animates the panel's height from
          0 to the natural content height (no JS measurement). Items stay in
          DOM so the container's width is locked regardless of state. */}
      <div
        style={{
          display: 'grid',
          width: '100%',
          gridTemplateRows: expanded ? '1fr' : '0fr',
          transition: 'grid-template-rows 200ms cubic-bezier(0.215, 0.61, 0.355, 1)',
        }}
      >
        <div className="overflow-hidden" style={{ minHeight: 0 }}>
          <div className="pt-lg">
            <ItemsList />
          </div>
        </div>
      </div>
    </div>
  );
};
